<template>
  <div class="configurePageContainer" style="height: 100%; width: 100%">
    <div v-if="readPermission" class="createCategoryBody">
      <div class="createCategoryHeader">
        <svg @click="cancelAdd" style="cursor: pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
          <path d="M5.25 11.25H20.25C20.45 11.25 20.64 11.33 20.78 11.47C20.92 11.61 21 11.8 21 12C21 12.2 20.92 12.39 20.78 12.53C20.64 12.67 20.45 12.75 20.25 12.75H5.25C5.05 12.75 4.86 12.67 4.72 12.53C4.58 12.39 4.5 12.2 4.5 12C4.5 11.8 4.58 11.61 4.72 11.47C4.86 11.33 5.05 11.25 5.25 11.25Z" fill="black" />
          <path d="M5.56 12L11.78 18.22C11.92 18.36 12 18.55 12 18.75C12 18.95 11.92 19.14 11.78 19.28C11.64 19.42 11.45 19.5 11.25 19.5C11.05 19.5 10.86 19.42 10.72 19.28L3.97 12.53C3.9 12.46 3.84 12.38 3.81 12.29C3.77 12.2 3.75 12.1 3.75 12C3.75 11.9 3.77 11.8 3.81 11.71C3.84 11.62 3.9 11.54 3.97 11.47L10.72 4.72C10.86 4.58 11.05 4.5 11.25 4.5C11.45 4.5 11.64 4.58 11.78 4.72C11.92 4.86 12 5.05 12 5.25C12 5.45 11.92 5.64 11.78 5.78L5.56 12Z" fill="black" />
        </svg>
        <span class="newPageTitle">{{ this.editMode ? $t('categories.forms.edit_title') : $t('categories.forms.add_title') }}</span>
      </div>
      <div class="form-grid">
        <div class="form-field">
          <label>{{ $t('categories.forms.labels.tenant_name') }}</label>
          <select class="custom-select-trigger" v-model="category.tenant_id" :disabled="editMode">
            <option v-if="userRole === 'Super Admin'" value="master">{{ $t('categories.forms.options.master_tenant') }}</option>
            <option v-for="tenant in activeTenants" :value="tenant.value" :key="tenant.value">
              {{ tenant?.label }}
            </option>
          </select>
        </div>

        <div class="form-field">
          <label>{{ $t('categories.forms.labels.primary_classification_name') }}</label>
          <template v-if="category.tenant_id !== 'master'">
            <select class="custom-select-trigger" v-model="category.category_id" :class="{ 'error-border': showErrors && errors.category_id }">
              <option value="">{{ $t('categories.forms.placeholders.select_primary_classification') }}</option>
              <option v-for="category in masterCategories" :key="category.id" :value="category.id">
                {{ category.category_name }}
              </option>
            </select>
            <span class="error-text" v-if="showErrors && errors.category_id">
              {{ errors.category_id }}
            </span>
          </template>
          <template v-else>
            <input v-model="category.category_name" type="text" :class="{ 'error-border': showErrors && errors.category_name }" :placeholder="$t('categories.forms.placeholders.enter_primary_classification_name')" @input="handleFieldChange('category_name')" />
            <span class="error-text" v-if="showErrors && errors.category_name">
              {{ errors.category_name }}
            </span>
          </template>
        </div>
        <div class="form-field" v-if="category.tenant_id !== 'master'">
          <label>{{ $t('categories.forms.labels.category_display_name') }} </label>
          <input
            v-model="category.category_display_name"
            type="text"
            :class="{
              'error-border': showErrors && errors.category_display_name,
            }"
            :placeholder="$t('categories.forms.placeholders.enter_category_display_name')"
            @input="handleFieldChange('category_display_name')"
          />
          <span class="error-text" v-if="showErrors && errors.category_display_name">
            {{ errors.category_display_name }}
          </span>
        </div>
        <div class="form-field">
          <label>{{ $t('categories.forms.labels.status') }}</label>
          <IsActiveComponentV1 :modelValue="category.is_active" @update:modelValue="handleIsActiveChange" @change="handleIsActiveChange" />
        </div>
      </div>

      <div class="button-group">
        <button class="cancel-btn" @click="cancelAdd">{{ $t('categories.buttons.cancel') }}</button>
        <button v-if="writePermission" class="save-btn" @click="saveCategory" :disabled="loadingStates.saveCategory || !writePermission">
          {{ loadingStates.saveCategory ? $t('editTenant.saving') : $t('categories.buttons.save') }}
        </button>
      </div>
    </div>
    <div v-else>
      <PermissionAlert :message="$t('common.no_permission')" />
    </div>
  </div>
</template>

<script>
import { get, put, post } from '@/utils/api'
import IsActiveComponentV1 from '@/components/IsActiveComponentV1/IsActiveComponentV1.vue'
import { showToast } from '@/utils/toast'
import PermissionAlert from '@/components/PermissionAlert.vue'

export default {
  components: {
    IsActiveComponentV1,
    PermissionAlert,
  },
  data() {
    return {
      readPermission: false,
      writePermission: false,
      readPermissionMaster: false,
      writePermissionMaster: false,
      userRole: localStorage.getItem('userRole'),
      category: {
        category_display_name: '',
        is_active: true,
        tenant_id: '',
        category_id: '',
        category_name: '',
      },
      editMode: false,
      showErrors: false,
      errors: {},
      tenants: [],
      masterCategories: [],
      loadingStates: {
        categories: false,
        tenants: false,
        saveCategory: false,
        updateCategory: false,
      },
      tenantId: null,
    }
  },
  mounted() {
    const { tenantId } = this.$route.query
    const categoryId = this.$route.params.id

    this.tenantId = tenantId
    this.editMode = categoryId !== 'add'

    this.checkPermissions()
    this.fetchTenants()
    this.fetchCategories()

    if (this.editMode && categoryId) {
      this.fetchCategoryById(categoryId)
    } else {
      if (tenantId !== 'master') this.category.tenant_id = parseInt(tenantId)
      else this.category.tenant_id = tenantId
    }
  },
  computed: {
    activeTenants() {
      return this.tenants.filter((tenant) => tenant.is_active === true || tenant.id === this.category.tenant_id)
    },
  },
  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')

      // Check for master tenant permissions
      this.readPermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_RD' && p.is_active)
      this.writePermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_WR' && p.is_active)
      // Check for tenant permissions
      this.readPermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_WR' && p.is_active)
    },
    async fetchCategoryById(id) {
      try {
        this.loadingStates.categories = true
        const url = this.tenantId === 'master' ? `/v1/categories/${id}` : `/v1/tenant-categories/${id}`

        const response = await get(url)
        const { data } = response.data

        if (data) {
          const tenant_id = data.tenant_id || 'master'
          data.tenant_id = tenant_id
          this.category = data
        }
      } catch (error) {
        showToast({ title: this.$t('common.error'), message: this.$t('errors.failed_to_fetch_category'), type: 'error' })
      } finally {
        this.loadingStates.categories = false
      }
    },

    async fetchTenants() {
      try {
        this.loadingStates.tenants = true
        const response = await get('/v1/tenants?page=1&pageSize=100000')
        if (response && response.data) {
          this.tenants = response.data.data.tenants.map((t) => ({
            value: t.id,
            label: t.tenant_name,
            is_active: t.is_active,
          }))
        }
      } catch (error) {
        showToast({ title: this.$t('common.error'), message: this.$t('errors.failed_to_fetch_tenants'), type: 'error' })
      } finally {
        this.loadingStates.tenants = false
      }
    },

    async fetchCategories() {
      try {
        const response = await get(`v1/categories?page=1&pageSize=100000`)
        const data = response.data.data.data
        if (response && data) {
          // filter master category - remove bedrock and textract categories from master
          this.masterCategories = data?.filter((cat) => {
            const catCondition = cat.category_name !== 'Bedrock Training' && cat.category_name !== 'Bedrock Documents' && cat.category_name !== 'Textract Training'
            if (catCondition) {
              return cat
            }
          })
        }
      } catch (error) {
        showToast({ title: this.$t('common.error'), message: this.$t('errors.failed_to_load_categories'), type: 'error' })
      }
    },

    cancelAdd() {
      this.$router.go(-1)
    },

    validateForm() {
      this.errors = {}
      const { tenant_id, category_name, category_id, category_display_name } = this.category || {}

      // Master tenant category validation
      if (tenant_id === 'master') {
        if (!category_name?.trim()) {
          this.errors.category_name = this.$t('categories.forms.validation.primary_classification_required')
          return false
        }
        return true
      }

      // Tenant-specific category validation
      let isValid = true

      if (!category_id) {
        this.errors.category_id = this.$t('categories.forms.validation.category_id_required')
        isValid = false
      }

      if (!category_display_name?.trim()) {
        this.errors.category_display_name = this.$t('categories.forms.validation.category_display_name_required')
        isValid = false
      }

      return isValid
    },

    async saveCategory() {
      this.showErrors = true

      if (!this.validateForm()) {
        showToast({
          title: this.$t('categories.messages.validation_error_title'),
          message: this.$t('categories.forms.validation.fill_required_fields'),
          type: 'error',
        })
        return
      }

      this.loadingStates.saveCategory = true

      const isMasterTenant = this.category.tenant_id === 'master'
      const isEdit = this.editMode
      const baseUrl = isMasterTenant ? '/v1/categories' : '/v1/tenant-categories'
      const url = isEdit ? `${baseUrl}/${this.category.id}` : baseUrl
      const httpMethod = isEdit ? put : post

      try {
        const response = await httpMethod(url, this.category)
        const isSuccess = [200, 201].includes(response?.data?.code)

        if (isSuccess) {
          showToast({
            title: this.$t('categories.messages.success_title'),
            message: isEdit ? this.$t('categories.messages.success.updated') : this.$t('categories.messages.success.created'),
            type: 'success',
          })
          this.showAddForm = false
          this.fetchCategories()
          this.cancelAdd()
        }
      } catch (error) {
        const rawMessage = error.response?.data?.message || ''
        let userMessage = isEdit ? this.$t('categories.messages.error.update_failed') : this.$t('categories.messages.error.create_failed')

        if (rawMessage.includes('duplicate key')) {
          if (rawMessage.includes('uni_categories_category_name')) {
            userMessage = this.$t('categories.forms.validation.duplicate_category_name')
          } else if (rawMessage.includes('uq_tenant_category')) {
            userMessage = this.$t('categories.forms.validation.duplicate_tenant_category')
          }
        }

        showToast({
          title: this.$t('categories.messages.error_title'),
          message: userMessage,
          type: 'error',
        })
      } finally {
        this.loadingStates.saveCategory = false
      }
    },

    handleFieldChange(fieldName) {
      this.showErrors = false
      this.errors[fieldName] = ''
    },

    handleIsActiveChange(value) {
      this.category.is_active = value
    },
  },
}
</script>

<style scoped>
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 25px;
  margin-bottom: 25px;
}

.createCategoryBody {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.createCategoryHeader {
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 9px;
}

.button-group {
  margin-top: 20px;
  display: flex;
  gap: 20px;
}

.cancel-btn {
  padding: 10px 32px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  font-family: Archivo;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
}

.save-btn {
  padding: 10px 32px;
  background: var(--primary-color);
  color: white;
  font-family: Archivo;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.form-field label {
  color: #8b8888;
  font-family: Archivo;
  font-size: 18px;
  font-weight: 400;
  display: block;
  margin-bottom: 6px;
}

.form-field input,
.form-field select {
  width: 100%;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.newPageTitle {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 28px;
  font-weight: 500;
  line-height: 150%;
}

/* Optional: Highlight on focus */
select:focus {
  outline: none;
  border-color: #667085;
  box-shadow: 0 0 0 1px #667085;
}

/* Error state */
.dropdown-error {
  border-color: #e50006 !important;
}

.error-border {
  border-color: #dc3545 !important;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  display: block;
  font-family: Archivo;
}
</style>
